import {
  CLASS_BOOK_AHEAD_DAYS,
  CLASS_CANCEL_CUTOFF_HOURS,
  daysWords,
  hoursWords,
  type ClassRules,
  type ClassRulesView,
} from "@teamna/shared";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { InlineError, SelectField } from "../components/index.ts";

/** The select value for "Business default". */
const DEFAULT = "default";

function toValue(value: number | null): string {
  return value === null ? DEFAULT : String(value);
}

function fromValue(value: string): number | null {
  return value === DEFAULT ? null : Number(value);
}

/**
 * "Booking rules for this class" on the class panel (epic "Pilates studio shape", B3). Two choices:
 * how many days ahead members can book, and until when a cancel is free. "Business default" follows
 * the business rules (Packages and rules) and says their value. A studio opens private sessions 14
 * or 30 days ahead here, and its group classes keep the default. The owner and admins only; the
 * phone has no class rules (an office job, decision D1).
 */
export function ClassRulesSection({
  business,
  value,
  onChange,
  loading = false,
  error = null,
}: {
  /** The business values that "Business default" follows. */
  business: ClassRulesView["business"] | null;
  value: ClassRules;
  onChange: (next: ClassRules) => void;
  loading?: boolean;
  error?: unknown;
}) {
  const { label } = useLabels();
  const group = label("group");
  const members = label("member", { plural: true, capital: true });

  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0" aria-busy={loading || undefined}>
      <legend className="mb-1 text-[13px] font-semibold text-ink">Booking rules for this {group}</legend>
      {error ? <InlineError error={error} /> : null}
      <SelectField
        label={`${members} can book up to`}
        hint={`How many days before a session ${label("member", { plural: true })} can book it.`}
        value={toValue(value.maxBookAheadDays)}
        disabled={loading || business === null}
        onChange={(event) => onChange({ ...value, maxBookAheadDays: fromValue(event.target.value) })}
      >
        <option value={DEFAULT}>
          Business default{business ? ` (${daysWords(business.maxBookAheadDays)} ahead)` : ""}
        </option>
        {CLASS_BOOK_AHEAD_DAYS.map((days) => (
          <option key={days} value={String(days)}>
            {daysWords(days)} ahead
          </option>
        ))}
      </SelectField>
      <SelectField
        label="Free cancel until"
        hint="Up to this time, a cancel gives the session back."
        value={toValue(value.cancelCutoffHours)}
        disabled={loading || business === null}
        onChange={(event) => onChange({ ...value, cancelCutoffHours: fromValue(event.target.value) })}
      >
        <option value={DEFAULT}>
          Business default{business ? ` (${hoursWords(business.cancelCutoffHours)} before)` : ""}
        </option>
        {CLASS_CANCEL_CUTOFF_HOURS.map((hours) => (
          <option key={hours} value={String(hours)}>
            {hoursWords(hours)} before
          </option>
        ))}
      </SelectField>
      <p className="m-0 text-[12.5px] text-ink-2">
        Business default follows the rules on Packages and rules. Other {label("group", { plural: true })} do not change.
      </p>
    </fieldset>
  );
}
